import { Deserializable } from '../../protocols/deserializable';
import { MenuStyleObject } from '../../enum/dto/menu-style-object.enum';
import { UniquelyIdentifiable } from '../../protocols/uniquely-identifiable';
import { VariantBadge } from '../../product/dto/variant-badge';
import { SortUtils } from '../../../utils/sort-utils';
import { Terpene } from '../../enum/shared/terpene';
import { StringUtils } from '../../../utils/string-utils';
import { Cannabinoid, PrimaryCannabinoid, SecondaryCannabinoid } from '../../enum/shared/cannabinoid';
import { exists } from '../../../functions/exists';
import { memoize } from '../../../modules/shared/decorators/memoize';
import { cacheResult } from '../../../modules/shared/decorators/cache-result';
import { EnumUtils } from '../../../utils/enum-utils';
import type { Variant } from '../../product/dto/variant';

export class DisplayAttribute implements Deserializable, UniquelyIdentifiable {
  constructor(companyId?: number, id?: string, objectType?: MenuStyleObject) {
    this.companyId = companyId;
    this.id = id;
    this.objectType = objectType;
    this.displayName = '';
    this.initializeCannabinoidsAndTerpeneProperties();
  }

  @cacheResult
  static cannabinoidProperties(): string[] {
    const properties: string[] = [];
    EnumUtils.getCannabinoids()?.forEach(cannabinoid => {
      properties.push(cannabinoid);
      properties.push(`min${cannabinoid}`);
      properties.push(`max${cannabinoid}`);
    });
    return properties;
  }

  companyId: number;
  id: string;
  locationId: number;
  objectId: string;
  objectType: MenuStyleObject;
  displayName: string;
  minTHC: string;
  maxTHC: string;
  THC: string;
  CBD: string;
  minCBD: string;
  maxCBD: string;
  TAC: string;
  minTAC: string;
  maxTAC: string;
  badgeIds: string[];
  badges: VariantBadge[];
  defaultLabel: string;
  topCannabinoid: Cannabinoid;
  presentCannabinoids: Cannabinoid[];
  totalTerpene: string;
  minTotalTerpene: string;
  maxTotalTerpene: string;
  topTerpene: string;
  presentTerpenes: Terpene[];

  // Inherited
  inheritedDisplayAttribute: DisplayAttribute;

  public onDeserialize() {
    const Deserialize = window?.injector?.Deserialize;
    const inheritedDA = this.inheritedDisplayAttribute;
    this.inheritedDisplayAttribute = Deserialize?.instanceOf(DisplayAttribute, inheritedDA);
    this.badgeIds = Array.from(this.badgeIds || []);
    this.badges = Deserialize?.arrayOf(VariantBadge, this.badges);
    this.initializeCannabinoidsAndTerpeneProperties();
  }

  /**
   * Adds [cannabinoid], [minCannabinoid], and [maxCannabinoid] properties to the DisplayAttribute object.
   * Adds [terpene], [minTerpene], and [maxTerpene] properties to the DisplayAttribute object.
   * NOTE: terpene enums are capitalized, but terpene properties are camelCase.
   */
  protected initializeCannabinoidsAndTerpeneProperties(): void {
    EnumUtils.getCannabinoids()?.forEach(cannabinoid => {
      this[cannabinoid] = this[cannabinoid] || '';
      this[`min${cannabinoid}`] = this[`min${cannabinoid}`] ?? null;
      this[`max${cannabinoid}`] = this[`max${cannabinoid}`] ?? null;
    });
    this.presentCannabinoids = Array.from(this.presentCannabinoids || []);
    this.presentTerpenes = Array.from(this.presentTerpenes || []);
    EnumUtils.getTerpenes()?.forEach(terpene => {
      const camelCaseTerpene = StringUtils.camelize(terpene);
      const pascalCaseTerpene = StringUtils.toPascalCase(terpene);
      this[camelCaseTerpene] = this[camelCaseTerpene] || null;
      this[`min${pascalCaseTerpene}`] = this[`min${pascalCaseTerpene}`] ?? null;
      this[`max${pascalCaseTerpene}`] = this[`max${pascalCaseTerpene}`] ?? null;
    });
  }

  public getDisplayName(): string {
    return this.displayName || this.inheritedDisplayAttribute?.displayName || null;
  }

  @memoize
  public getAutoCalculatedTopTerpene(useTerpeneRange: boolean, enabledTerpenesPascalCased: string[]): string {
    const top: { terpene: string; value: number } = { terpene: undefined, value: undefined };
    enabledTerpenesPascalCased?.forEach(terpenePascalCased => {
      const accessor = useTerpeneRange ? `max${terpenePascalCased}` : StringUtils.camelize(terpenePascalCased);
      const value = this[accessor] || this.inheritedDisplayAttribute?.[accessor];
      if (exists(value)) {
        const valueAsNumber = parseFloat(value);
        const isFiniteAndLargerThanZero = Number.isFinite(valueAsNumber) && valueAsNumber > 0;
        if (isFiniteAndLargerThanZero && (valueAsNumber > top.value || top.value === undefined)) {
          top.terpene = StringUtils.splitPascalCasePreserveAcronyms(terpenePascalCased);
          top.value = valueAsNumber;
        }
      }
    });
    return top.terpene;
  }

  @memoize
  public getAutoCalculatedPresentTerpenes(useTerpeneRange: boolean, enabledTerpenesPascalCased: string[]): Terpene[] {
    return (
      enabledTerpenesPascalCased
        ?.filter(terpenePascalCased => {
          if (useTerpeneRange) {
            const minTerpeneValue =
              this[`min${terpenePascalCased}`] || this.inheritedDisplayAttribute?.[`min${terpenePascalCased}`];
            const hasMinActiveCannabinoid = this.valueIsGreaterThanZero(minTerpeneValue);
            const maxTerpeneValue =
              this[`max${terpenePascalCased}`] || this.inheritedDisplayAttribute?.[`max${terpenePascalCased}`];
            const hasMaxActiveCannabinoid = this.valueIsGreaterThanZero(maxTerpeneValue);
            return hasMinActiveCannabinoid || hasMaxActiveCannabinoid;
          } else {
            const terpeneCamelCased = StringUtils.pascalCaseToCamelCase(terpenePascalCased);
            const terpeneValue = this[terpeneCamelCased] || this.inheritedDisplayAttribute?.[terpeneCamelCased];
            return this.valueIsGreaterThanZero(terpeneValue);
          }
        })
        ?.map(presentTerpenePascalCased => {
          return StringUtils.splitPascalCasePreserveAcronyms(presentTerpenePascalCased) as Terpene;
        }) || []
    );
  }

  @memoize
  public getAutoCalculatedPresentCannabinoids(
    variant: Variant,
    useCannabinoidRange: boolean,
    enabledCannabinoids: string[]
  ): Cannabinoid[] {
    return (
      enabledCannabinoids
        ?.filter(enabledCannabinoid => {
          if (useCannabinoidRange) {
            const min = `min${enabledCannabinoid}`;
            const minCannabinoidValue = this[min] || this.inheritedDisplayAttribute?.[min] || variant?.[min];
            const hasMinActiveCannabinoid = this.valueIsGreaterThanZero(minCannabinoidValue);
            const max = `max${enabledCannabinoid}`;
            const maxCannabinoidValue = this[max] || this.inheritedDisplayAttribute?.[max] || variant?.[max];
            const hasMaxActiveCannabinoid = this.valueIsGreaterThanZero(maxCannabinoidValue);
            return hasMinActiveCannabinoid || hasMaxActiveCannabinoid;
          } else {
            const cannabinoidValue =
              this[enabledCannabinoid] ||
              this.inheritedDisplayAttribute?.[enabledCannabinoid] ||
              variant?.[enabledCannabinoid];
            return this.valueIsGreaterThanZero(cannabinoidValue);
          }
        })
        ?.map(presentCannabinoid => {
          return presentCannabinoid as Cannabinoid;
        }) || []
    );
  }

  @memoize
  public getAutoCalculatedTopCannabinoid(
    useCannabinoidRange: boolean,
    enabledSecondaryCannabinoids: SecondaryCannabinoid[],
    ignoredListCannabinoids: Cannabinoid[]
  ): string {
    const top: { cannabinoid: string; value: number } = { cannabinoid: undefined, value: undefined };
    const enabledCannabinoids: Cannabinoid[] = [
      PrimaryCannabinoid.THC,
      PrimaryCannabinoid.CBD,
      ...(enabledSecondaryCannabinoids ?? [])
    ].filter(cannabinoid => {
      return !ignoredListCannabinoids.includes(cannabinoid);
    });
    enabledCannabinoids?.forEach(cannabinoid => {
      const accessor = useCannabinoidRange ? `max${cannabinoid}` : cannabinoid;
      const value = this[accessor] || this.inheritedDisplayAttribute?.[accessor];
      if (exists(value)) {
        const valueAsNumber = parseFloat(value);
        const isFiniteAndLargerThanZero = Number.isFinite(valueAsNumber) && valueAsNumber > 0;
        if (isFiniteAndLargerThanZero && (valueAsNumber > top.value || top.value === undefined)) {
          top.cannabinoid = cannabinoid;
          top.value = valueAsNumber;
        }
      }
    });
    return top.cannabinoid;
  }

  protected valueIsGreaterThanZero(value: string): boolean {
    return parseFloat(value) > 0;
  }

  @memoize
  public getBadges(): VariantBadge[] {
    const displayAttributeBadges = this.badges ?? [];
    const inheritedDisplayAttributeBadges = this.inheritedDisplayAttribute?.badges ?? [];
    if (displayAttributeBadges.length > 0) {
      return displayAttributeBadges;
    } else if (inheritedDisplayAttributeBadges.length > 0) {
      return inheritedDisplayAttributeBadges;
    } else {
      return [];
    }
  }

  isCompanyDA(): boolean {
    return this.companyId === this.locationId;
  }

  isLocationDA(): boolean {
    return this.companyId !== this.locationId;
  }

  /**
   * Cannabinoids are stored as strings, therefore, we need to parse their numerical values,
   * round them, and then replace the old string number with the new rounded string number.
   */
  roundCannabinoids(shouldRound: boolean): void {
    if (!shouldRound) return;
    const cannabinoidProperties = DisplayAttribute.cannabinoidProperties();
    cannabinoidProperties.forEach(prop => {
      const cannabinoidString = this[prop]?.match(/(\d+\.?\d*)/)?.[0];
      const roundedCannabinoidNumber = Math.round(parseFloat(this[prop]));
      if (Number.isFinite(roundedCannabinoidNumber)) {
        const rounded = roundedCannabinoidNumber?.toString(10);
        this[prop] = (this[prop] as string)?.replace(cannabinoidString, rounded);
      }
    });
    // this will keep chaining upwards for all linked inherited display attributes and stop when chain is broken
    this.inheritedDisplayAttribute?.roundCannabinoids(shouldRound);
  }

  getUniqueIdentifier(): string {
    let uniqueId = `
      -${this.companyId}
      -${this.id}
      -${this.locationId}
      -${this.objectId}
      -${this.objectType}
      -${this.displayName}
      -${this.totalTerpene}
      -${this.minTotalTerpene}
      -${this.maxTotalTerpene}
      -${this.topTerpene}
      -${this.badgeIds?.sort()?.join(',')}
      -${this.badges
        ?.sort(SortUtils.sortBadges)
        ?.map(b => b?.getUniqueIdentifier())
        ?.join(',')}
      -${this.inheritedDisplayAttribute?.getUniqueIdentifier()}
    `;
    EnumUtils.getCannabinoids()?.forEach(cannabinoid => {
      uniqueId += `-${this[cannabinoid]}`;
      uniqueId += `-${this[`min${cannabinoid}`]}`;
      uniqueId += `-${this[`max${cannabinoid}`]}`;
    });
    EnumUtils.getTerpenesCamelCasedAndPascalCased()?.forEach(terp => {
      uniqueId += `-${this[terp.camelCase]}`;
      uniqueId += `-${this[`min${terp.pascalCase}`]}`;
      uniqueId += `-${this[`max${terp.pascalCase}`]}`;
    });
    return uniqueId;
  }
}
